export type ConstructorLocale = 'en' | 'ru';

export const LOCALE_STORAGE_KEY = 'mv-constructor-locale';

export function detectConstructorLocale(): ConstructorLocale {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored === 'en' || stored === 'ru') return stored;
  }
  if (typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('ru')) {
    return 'ru';
  }
  return 'en';
}

const en = {
  pages: 'Pages',
  layers: 'Layers',
  library: 'Library',
  assets: 'Assets',
  inspector: 'Inspector',
  nothingSelected: 'No selection',
  pageSetup: 'Page setup',
  designTokens: 'Design tokens',
  inserted: 'Inserted',
  dishList: 'Dish list',
  sectionHeader: 'Section header',
  twoColumns: 'Two columns',
  dishPlusPrice: 'Dish + price',
  customValue: 'Custom',
  variantKey: 'Variant key',
  weight: 'Weight (how often this layout is picked)',
  label: 'Label',
  clearPage: 'Clear this page',
  clearAllPages: 'Clear all pages',
  clearPageConfirm: 'Remove every block on this page?',
  clearAllConfirm: 'Clear every page variant? This cannot be undone from the canvas (Undo still works).',
  delete: 'Delete',
  hide: 'Hide',
  show: 'Show',
  selectTool: 'Select',
  handTool: 'Hand',
  frameTool: 'Frame',
  textTool: 'Text',
  rectTool: 'Rectangle',
  ellipseTool: 'Ellipse',
  lineTool: 'Line',
  imageTool: 'Image',
  cover: 'Cover',
  contents: 'Contents',
  sectionDivider: 'Section divider',
  categoryPages: 'Category pages',
  spotlight: 'Spotlight',
  filler: 'Filler',
  infoPage: 'Info page',
  backCover: 'Back cover',
  previewData: 'Preview data',
  typical: 'Typical menu',
  sparse: 'Few dishes',
  dense: 'Packed menu',
  noPhotos: 'No photos',
  sampleData: 'Sample restaurant',
  liveMenu: 'Live menu',
  fitToWindow: 'Fit to window',
  pageOffscreen: 'Page is off-screen — fit to window',
  skipTour: 'Skip tour',
  next: 'Next',
  getStarted: 'Get started',
  coachBanner: 'First pick a page type in Pages, then add blocks from Library. Use Layout wizard in More for a starter.',
  dismissHint: 'Got it',
  moreMenu: 'More',
  searchLayers: 'Search layers…',
  searchAssets: 'Search assets',
  addVariant: 'Add variant',
  referenceOverlay: 'Reference image',
  referenceUrl: 'Image URL',
  referenceOpacity: 'Opacity',
  backgroundFromUrl: 'Background image',
  dropToUploadAsset: 'Release to upload asset',
  dropOrClickUpload: 'Drop an image or click to upload',
  orPasteUrl: 'Or paste image URL',
  uploadingAsset: 'Uploading…',
  assetUploaded: 'Asset uploaded',
  assetsUploaded: '{count} assets uploaded',
  insertNode: 'Insert block',
  kindFrame: 'Frame',
  kindStack: 'Column / row',
  kindRepeater: 'Dish list',
  kindText: 'Text',
  kindImage: 'Image',
  kindGrid: 'Columns',
  kindDivider: 'Line',
  kindSpacer: 'Gap',
  kindShape: 'Shape',
  kindGroup: 'Group',
  kindSlot: 'Content hole',
  kindOverlay: 'Decoration',
  hintFrame: 'Box that can be a wrapper, category or dish',
  hintStack: 'A column or row of blocks',
  hintRepeater: 'Repeats once per dish or category',
  hintText: 'Name, price or heading',
  hintImage: 'Photo, logo or artwork',
  hintGrid: 'Two or more columns',
  hintDivider: 'Rule between sections',
  hintSpacer: 'Empty space',
  hintShape: 'Colour block',
  hintGroup: 'Styling wrapper',
  hintSlot: 'Named hole for page content',
  hintOverlay: 'Freely placed decoration',
  sourcePageItems: 'Dishes on this page',
  sourceMenuCategories: 'All categories',
  sourceCategoryItems: 'All dishes in the category',
  coverDishHint: 'Dish lists belong on a Category page — open Pages, then Library.',
  clickToEdit: 'Click to edit',
  language: 'Language',
  notUsedInSample: 'Not used in this sample',
  enAbbr: 'EN',
  ruAbbr: 'RU',
  switchToLight: 'Switch to light theme',
  switchToDark: 'Switch to dark theme',
  tourStep: 'Step',
  tourOf: 'of',
  tourWorkspaceTitle: 'Three-panel workspace',
  tourWorkspaceBody:
    'Pages, layers, library and assets live in the left sidebar. The inspector is always visible on the right.',
  tourToolsTitle: 'Tools & placement',
  tourToolsBody:
    'Use V, T, R, O, I, F shortcuts. Select a tool from the rail, then drag on the canvas to draw elements.',
  tourInspectorTitle: 'Inspector properties',
  tourInspectorBody:
    'Select any element to edit position, layout, appearance and data bindings in the right panel.',
  tourLibraryTitle: 'Component library',
  tourLibraryBody: 'Open the Library tab for menu presets and basic elements. Drag or click to insert.',
  tourPublishTitle: 'Preview & publish',
  tourPublishBody: 'Use the book icon to preview the full menu flow. Publish when the template is ready.',
  searchLibrary: 'Search library',
  menuBlocks: 'Menu blocks',
  yours: 'Yours',
  elements: 'Elements',
  savePreset: 'Save selection as preset',
  selectVariant: 'Select a page variant',
  duplicateVariant: 'Duplicate variant',
  deleteVariant: 'Delete variant',
  libraryHint: 'Click a block to add it to the page. Drag onto the canvas to place it.',
  yourSnippet: 'Your snippet',
  insertTarget: 'insert',
  sheet: 'sheet',
  outputFormat: 'Output format',
  designVariant: 'Design variant',
  addFormat: 'Add format',
  removeFormat: 'Remove this format',
  resetFormatPreset: 'Reset to format size',
  pageSize: 'Page size',
  facingPages: 'Facing pages (spread)',
  facingPagesHint: 'Typical for books and bifold flyers. Screens stay single.',
  bleedHint: 'Print trim allowance. Leave 0 for screens.',
  safeAreaHint: 'Keep important content inside this inset.',
  bookFlow: 'Book flow',
  tentFlow: 'Table tent',
  tentFront: 'Front panel',
  tentBack: 'Back panel',
  pickFormatToAdd: 'Choose a format to add',
  noFormatsLeft: 'All nine formats are already on this template.',
  formatCanvasOverride: 'Width and height are design units. The label above is the real paper or screen size.',
  back: 'Back',
  publishedStatus: 'published',
  draftStatus: 'draft',
  undo: 'Undo ⌘Z',
  redo: 'Redo ⇧⌘Z',
  bookFlowPreview: 'Book flow preview',
  save: 'Save',
  saveLocalOnly: 'Save locally only ⌘S',
  saveShortcut: 'Save ⌘S',
  publish: 'Publish',
  editCopy: 'Edit copy',
  createEditableDraft: 'Create an editable draft',
  publishDisabledSandbox: 'Publish is disabled in sandbox',
  stressTest: 'Stress test',
  layoutWizard: 'Layout wizard',
  createWithAi: 'Create with AI',
  editWithAi: 'Edit with AI',
  aiEditSubtitle: 'Live surgery on the open design',
  aiEditPulse: 'Design Pulse',
  aiEditPulseHint: 'Layout health before and after the edit',
  aiEditPulseDelta: '{before} → {after}',
  aiEditCombo: 'Combo ×{n}',
  aiEditMissions: 'Missions',
  aiMissionTighten: 'Tighten spacing',
  aiMissionWarmer: 'Warmer palette',
  aiMissionContrast: 'Fix contrast',
  aiMissionPhoto: 'More photo-forward',
  aiMissionCover: 'Cleaner cover',
  aiEditPrompt: 'Instruction',
  aiEditPromptPlaceholder: 'e.g. Warm the palette and tighten category lists…',
  aiEditScopeAll: 'All',
  aiEditScopeTokens: 'Tokens',
  aiEditScopePage: 'Page',
  aiEditScopeSelection: 'Selection',
  aiEditRun: 'Run edit',
  aiEditWorking: 'Editing…',
  aiEditAccept: 'Accept all',
  aiEditReject: 'Reject',
  aiEditCancel: 'Cancel',
  aiEditPlan: 'Plan',
  aiEditOps: '{n} changes',
  aiEditEndCard: '{n} moves · Pulse {delta}',
  aiEditStartFailed: 'Could not start AI edit.',
  aiEditFailed: 'AI edit failed.',
  aiEditStageScanning: 'Scanning design health…',
  aiEditStagePlanning: 'Planning moves…',
  aiEditStageEditing: 'Applying edits…',
  aiEditStageDiffing: 'Mapping changes…',
  aiEditStageValidate: 'Validating…',
  aiEditSelection: 'Edit selection with AI',
  aiStudioTitle: 'AI Studio',
  aiStudioSubtitle: 'Editable Menuvera template — not a flat image',
  aiStudioKicker: 'Design generation',
  aiBriefHeadline: 'Describe the menu you want',
  aiBriefLead: 'Art direction, mood, and format. AI builds a full editable book you can refine and accept.',
  aiPromptLabel: 'Brief',
  aiPromptPlaceholder:
    'e.g. Premium Japanese omakase menu book — restrained, charcoal and warm paper, editorial typography',
  aiFormatLabel: 'Format',
  aiEmphasisLabel: 'Emphasis',
  aiFormatBook: 'Book',
  aiFormatTv: 'TV',
  aiFormatSocial: 'Social',
  aiFormatPocket: 'Pocket',
  aiFormatInterior: 'Interior',
  aiEmphasisBalanced: 'Balanced',
  aiEmphasisCover: 'Cover-heavy',
  aiEmphasisPhoto: 'Photo-forward',
  aiEmphasisText: 'Text-dense',
  aiTwoConcepts: 'Generate 2 concepts',
  aiTwoConceptsHint: 'Compare directions side by side, then pick one',
  aiAssetsLabel: 'Platform assets (optional)',
  aiGenerate: 'Generate',
  aiStarting: 'Starting…',
  aiSkipQuestions: 'Skip clarifying questions',
  aiClose: 'Close',
  aiUnlimited: 'unlimited',
  aiQuotaToday: '{remaining}/{limit} today',
  aiDisabledPublished: 'Published versions are read-only. Create a draft first.',
  aiQuotaReached: 'Daily AI design quota reached.',
  aiUnavailable: 'AI Design is unavailable.',
  aiProgressTitle: 'Pipeline',
  aiStageQueued: 'Queued',
  aiStageConcept: 'Concept',
  aiStageLayout: 'Layout',
  aiStageValidate: 'Validate',
  aiStageQuality: 'Quality',
  aiStagePreview: 'Preview',
  aiGeneratingHeadline: 'Building your design',
  aiGeneratingLead: 'Concept, layout, and quality checks run in sequence. This stays editable.',
  aiClarifyTitle: 'A few details',
  aiClarifyHint: 'Answers steer art direction before layout starts.',
  aiClarifyPlaceholder: 'Your answer',
  aiClarifyStageHeadline: 'Need a bit more direction',
  aiClarifyStageLead: 'Answer on the left, then continue generation.',
  aiContinue: 'Continue',
  aiContinuing: 'Continuing…',
  aiCancelGeneration: 'Cancel generation',
  aiNewBrief: 'New brief',
  aiFailed: 'Generation failed.',
  aiFailedHeadline: 'Something went wrong',
  aiDirectionTitle: 'Direction',
  aiRefineLabel: 'Refine',
  aiRefinePlaceholder: 'Make it more premium and restrained…',
  aiRefine: 'Refine',
  aiRefining: 'Refining…',
  aiRegenerate: 'Regenerate',
  aiAccept: 'Accept into editor',
  aiReplaceDesign: 'Replace design',
  aiDirtyConfirm: 'You have unsaved edits. Accepting replaces the current document — Undo restores it.',
  aiCancel: 'Cancel',
  aiConceptN: 'Concept {n}',
  aiNoPreview: 'No preview pages yet.',
  compareFormats: 'Compare formats',
  compareFormatsNeedTwo: 'Add a second format variant in Design tokens → Page to compare side by side.',
  compareFormatsEmptyTitle: 'Nothing to compare yet',
  saveCheckpoint: 'Save checkpoint',
  restoreCheckpoint: 'Restore',
  createDraftFromSandbox: 'Create draft from sandbox',
  exitSandbox: 'Exit sandbox',
  discardLocalChanges: 'Discard local changes',
  keyboardShortcuts: 'Keyboard shortcuts',
  editing: 'editing',
  pagesCount: 'pages',
  noPreview: 'No preview',
  issuesCount: 'issues',
  tools: 'Tools',
  toolHintSelect: 'Click to select · drag empty space to marquee',
  toolHintHand: 'Drag to pan the canvas',
  toolHintFrame: 'Drag on the canvas to draw a frame',
  toolHintText: 'Drag on the canvas to draw a text box',
  toolHintRect: 'Drag on the canvas to draw a rectangle',
  toolHintEllipse: 'Drag on the canvas to draw an ellipse',
  toolHintLine: 'Drag on the canvas to draw a line',
  toolHintImage: 'Drag on the canvas to place an image',
  alignSelection: 'Align selection',
  alignLeft: 'Align left',
  alignCenter: 'Align center',
  alignRight: 'Align right',
  alignTop: 'Align top',
  alignMiddle: 'Align middle',
  alignBottom: 'Align bottom',
  distributeHorizontal: 'Distribute horizontally',
  distributeVertical: 'Distribute vertically',
  selectionActions: 'Selection actions',
  duplicate: 'Duplicate',
  group: 'Group',
  ungroup: 'Ungroup',
  lock: 'Lock',
  unlock: 'Unlock',
  bringForward: 'Bring forward',
  sendBackward: 'Send backward',
  deleteLayerTitle: 'Delete layer?',
  deleteLayerDescription: 'This layer and its children will be removed.',
} as const;

const ru: Record<keyof typeof en, string> = {
  pages: 'Страницы',
  layers: 'Слои',
  library: 'Библиотека',
  assets: 'Файлы',
  inspector: 'Свойства',
  nothingSelected: 'Ничего не выбрано',
  pageSetup: 'Размер страницы',
  designTokens: 'Токены дизайна',
  inserted: 'Вставлено',
  dishList: 'Список блюд',
  sectionHeader: 'Заголовок секции',
  twoColumns: 'Две колонки',
  dishPlusPrice: 'Блюдо + цена',
  customValue: 'Своё',
  variantKey: 'Ключ варианта',
  weight: 'Вес (как часто выбирается этот макет)',
  label: 'Название',
  clearPage: 'Очистить страницу',
  clearAllPages: 'Очистить все страницы',
  clearPageConfirm: 'Удалить все блоки на этой странице?',
  clearAllConfirm: 'Очистить все варианты страниц? С холста это не вернуть (Ctrl+Z ещё работает).',
  delete: 'Удалить',
  hide: 'Скрыть',
  show: 'Показать',
  selectTool: 'Выбор',
  handTool: 'Рука',
  frameTool: 'Рамка',
  textTool: 'Текст',
  rectTool: 'Прямоугольник',
  ellipseTool: 'Овал',
  lineTool: 'Линия',
  imageTool: 'Картинка',
  cover: 'Обложка',
  contents: 'Содержание',
  sectionDivider: 'Разделитель секции',
  categoryPages: 'Страницы категории',
  spotlight: 'Акцент',
  filler: 'Заполнитель',
  infoPage: 'Инфо',
  backCover: 'Задняя обложка',
  previewData: 'Превью-данные',
  typical: 'Обычное меню',
  sparse: 'Мало блюд',
  dense: 'Плотное меню',
  noPhotos: 'Без фото',
  sampleData: 'Превью-ресторан',
  liveMenu: 'Живое меню',
  fitToWindow: 'Вписать в окно',
  pageOffscreen: 'Страница вне кадра — вписать в окно',
  skipTour: 'Пропустить',
  next: 'Дальше',
  getStarted: 'Начать',
  coachBanner: 'Сначала выберите тип листа в Pages, потом блоки в Library. Макет-мастер — в меню «Ещё».',
  dismissHint: 'Понятно',
  moreMenu: 'Ещё',
  searchLayers: 'Поиск слоёв…',
  searchAssets: 'Поиск файлов',
  addVariant: 'Добавить вариант',
  referenceOverlay: 'Картинка-референс',
  referenceUrl: 'URL картинки',
  referenceOpacity: 'Прозрачность',
  backgroundFromUrl: 'Фоновое изображение',
  dropToUploadAsset: 'Отпустите, чтобы загрузить ассет',
  dropOrClickUpload: 'Перетащите картинку или нажмите, чтобы загрузить',
  orPasteUrl: 'Или вставьте URL картинки',
  uploadingAsset: 'Загрузка…',
  assetUploaded: 'Ассет загружен',
  assetsUploaded: 'Загружено ассетов: {count}',
  insertNode: 'Вставить блок',
  kindFrame: 'Рамка',
  kindStack: 'Колонка / ряд',
  kindRepeater: 'Список блюд',
  kindText: 'Текст',
  kindImage: 'Картинка',
  kindGrid: 'Колонки',
  kindDivider: 'Линия',
  kindSpacer: 'Отступ',
  kindShape: 'Фигура',
  kindGroup: 'Группа',
  kindSlot: 'Окно контента',
  kindOverlay: 'Декор поверх',
  hintFrame: 'Контейнер: обёртка, категория или блюдо',
  hintStack: 'Колонка или ряд блоков',
  hintRepeater: 'Повторяется для каждого блюда или категории',
  hintText: 'Название, цена или заголовок',
  hintImage: 'Фото, логотип или рисунок',
  hintGrid: 'Две и больше колонок',
  hintDivider: 'Линия между секциями',
  hintSpacer: 'Пустое место',
  hintShape: 'Цветной блок',
  hintGroup: 'Обёртка для стиля',
  hintSlot: 'Именованное окно для контента',
  hintOverlay: 'Свободно расположенный декор',
  sourcePageItems: 'Блюда этой страницы',
  sourceMenuCategories: 'Все категории',
  sourceCategoryItems: 'Все блюда категории',
  coverDishHint: 'Список блюд живёт на странице категории — откройте Pages, затем Library.',
  clickToEdit: 'Нажмите, чтобы изменить',
  language: 'Язык',
  notUsedInSample: 'В этой выборке не используется',
  enAbbr: 'EN',
  ruAbbr: 'RU',
  switchToLight: 'Светлая тема',
  switchToDark: 'Тёмная тема',
  tourStep: 'Шаг',
  tourOf: 'из',
  tourWorkspaceTitle: 'Три панели',
  tourWorkspaceBody:
    'Страницы, слои, библиотека и файлы — слева. Свойства выбранного объекта всегда справа.',
  tourToolsTitle: 'Инструменты',
  tourToolsBody:
    'Клавиши V, T, R, O, I, F. Выберите инструмент на рейке, затем нарисуйте на холсте.',
  tourInspectorTitle: 'Свойства',
  tourInspectorBody:
    'Выберите элемент, чтобы править положение, макет, внешний вид и привязки данных.',
  tourLibraryTitle: 'Библиотека',
  tourLibraryBody: 'Во вкладке Library — готовые блоки меню. Клик или перетаскивание вставляет.',
  tourPublishTitle: 'Превью и публикация',
  tourPublishBody: 'Иконка книги показывает весь поток меню. Публикуйте, когда шаблон готов.',
  searchLibrary: 'Поиск в библиотеке',
  menuBlocks: 'Блоки меню',
  yours: 'Ваши',
  elements: 'Элементы',
  savePreset: 'Сохранить выделение как пресет',
  selectVariant: 'Выберите вариант страницы',
  duplicateVariant: 'Дублировать вариант',
  deleteVariant: 'Удалить вариант',
  libraryHint: 'Кликните блок, чтобы добавить на страницу. Перетащите на холст, чтобы поставить.',
  yourSnippet: 'Ваш фрагмент',
  insertTarget: 'вставка',
  sheet: 'лист',
  outputFormat: 'Формат выдачи',
  designVariant: 'Вариант дизайна',
  addFormat: 'Добавить формат',
  removeFormat: 'Удалить этот формат',
  resetFormatPreset: 'Сбросить размер формата',
  pageSize: 'Размер страницы',
  facingPages: 'Разворот (две страницы)',
  facingPagesHint: 'Нужен книгам и бифолд-флаерам. Экраны остаются одиночными.',
  bleedHint: 'Вылет под обрез. Для экранов — 0.',
  safeAreaHint: 'Важное не должно выходить за этот отступ.',
  bookFlow: 'Поток книги',
  tentFlow: 'Тейбл-тент',
  tentFront: 'Лицевая панель',
  tentBack: 'Оборотная панель',
  pickFormatToAdd: 'Какой формат добавить',
  noFormatsLeft: 'Все девять форматов уже есть в шаблоне.',
  formatCanvasOverride: 'Ширина и высота — единицы макета. Подпись выше — реальный размер бумаги или экрана.',
  back: 'Назад',
  publishedStatus: 'опубликован',
  draftStatus: 'черновик',
  undo: 'Отменить ⌘Z',
  redo: 'Повторить ⇧⌘Z',
  bookFlowPreview: 'Превью книги',
  save: 'Сохранить',
  saveLocalOnly: 'Только локально ⌘S',
  saveShortcut: 'Сохранить ⌘S',
  publish: 'Опубликовать',
  editCopy: 'Редактировать копию',
  createEditableDraft: 'Создать редактируемый черновик',
  publishDisabledSandbox: 'Публикация недоступна в песочнице',
  stressTest: 'Стресс-тест',
  layoutWizard: 'Мастер макета',
  createWithAi: 'Создать с AI',
  editWithAi: 'Редактировать с AI',
  aiEditSubtitle: 'Живая правка открытого дизайна',
  aiEditPulse: 'Design Pulse',
  aiEditPulseHint: 'Здоровье вёрстки до и после правки',
  aiEditPulseDelta: '{before} → {after}',
  aiEditCombo: 'Комбо ×{n}',
  aiEditMissions: 'Миссии',
  aiMissionTighten: 'Уплотнить отступы',
  aiMissionWarmer: 'Теплее палитра',
  aiMissionContrast: 'Исправить контраст',
  aiMissionPhoto: 'Больше фото',
  aiMissionCover: 'Чище обложка',
  aiEditPrompt: 'Инструкция',
  aiEditPromptPlaceholder: 'напр. Сделать палитру теплее и уплотнить списки…',
  aiEditScopeAll: 'Всё',
  aiEditScopeTokens: 'Токены',
  aiEditScopePage: 'Страница',
  aiEditScopeSelection: 'Выделение',
  aiEditRun: 'Запустить',
  aiEditWorking: 'Редактируем…',
  aiEditAccept: 'Принять всё',
  aiEditReject: 'Отклонить',
  aiEditCancel: 'Отмена',
  aiEditPlan: 'План',
  aiEditOps: '{n} изменений',
  aiEditEndCard: '{n} ходов · Pulse {delta}',
  aiEditStartFailed: 'Не удалось запустить AI-редактирование.',
  aiEditFailed: 'AI-редактирование не удалось.',
  aiEditStageScanning: 'Сканируем здоровье дизайна…',
  aiEditStagePlanning: 'Планируем правки…',
  aiEditStageEditing: 'Вносим изменения…',
  aiEditStageDiffing: 'Собираем diff…',
  aiEditStageValidate: 'Проверяем…',
  aiEditSelection: 'Редактировать выделение с AI',
  aiStudioTitle: 'AI Studio',
  aiStudioSubtitle: 'Редактируемый шаблон Menuvera — не картинка',
  aiStudioKicker: 'Генерация дизайна',
  aiBriefHeadline: 'Опишите меню, которое нужно',
  aiBriefLead: 'Настроение, стиль и формат. AI соберёт редактируемую книгу — можно уточнять и принять.',
  aiPromptLabel: 'Бриф',
  aiPromptPlaceholder:
    'напр. Премиум омакасе — сдержанно, уголь и тёплая бумага, редакционная типографика',
  aiFormatLabel: 'Формат',
  aiEmphasisLabel: 'Акцент',
  aiFormatBook: 'Книга',
  aiFormatTv: 'ТВ',
  aiFormatSocial: 'Соцсети',
  aiFormatPocket: 'Карман',
  aiFormatInterior: 'Интерьер',
  aiEmphasisBalanced: 'Баланс',
  aiEmphasisCover: 'Обложка',
  aiEmphasisPhoto: 'Фото',
  aiEmphasisText: 'Текст',
  aiTwoConcepts: 'Два концепта',
  aiTwoConceptsHint: 'Сравните направления рядом и выберите одно',
  aiAssetsLabel: 'Ассеты платформы (опционально)',
  aiGenerate: 'Сгенерировать',
  aiStarting: 'Запуск…',
  aiSkipQuestions: 'Пропустить уточнения',
  aiClose: 'Закрыть',
  aiUnlimited: 'без лимита',
  aiQuotaToday: '{remaining}/{limit} сегодня',
  aiDisabledPublished: 'Опубликованные версии только для чтения. Сначала создайте черновик.',
  aiQuotaReached: 'Дневная квота AI исчерпана.',
  aiUnavailable: 'AI Design недоступен.',
  aiProgressTitle: 'Этапы',
  aiStageQueued: 'В очереди',
  aiStageConcept: 'Концепт',
  aiStageLayout: 'Вёрстка',
  aiStageValidate: 'Проверка',
  aiStageQuality: 'Качество',
  aiStagePreview: 'Превью',
  aiGeneratingHeadline: 'Собираем дизайн',
  aiGeneratingLead: 'Концепт, вёрстка и проверки идут по очереди. Результат останется редактируемым.',
  aiClarifyTitle: 'Уточнения',
  aiClarifyHint: 'Ответы задают арт-дирекшн до начала вёрстки.',
  aiClarifyPlaceholder: 'Ваш ответ',
  aiClarifyStageHeadline: 'Нужно чуть больше направления',
  aiClarifyStageLead: 'Ответьте слева, затем продолжите генерацию.',
  aiContinue: 'Продолжить',
  aiContinuing: 'Продолжаем…',
  aiCancelGeneration: 'Отменить генерацию',
  aiNewBrief: 'Новый бриф',
  aiFailed: 'Генерация не удалась.',
  aiFailedHeadline: 'Что-то пошло не так',
  aiDirectionTitle: 'Направление',
  aiRefineLabel: 'Уточнить',
  aiRefinePlaceholder: 'Сделать более премиально и сдержанно…',
  aiRefine: 'Уточнить',
  aiRefining: 'Уточняем…',
  aiRegenerate: 'Заново',
  aiAccept: 'Принять в редактор',
  aiReplaceDesign: 'Заменить дизайн',
  aiDirtyConfirm: 'Есть несохранённые правки. Принятие заменит документ — Отмена вернёт его.',
  aiCancel: 'Отмена',
  aiConceptN: 'Концепт {n}',
  aiNoPreview: 'Пока нет страниц превью.',
  compareFormats: 'Сравнить форматы',
  compareFormatsNeedTwo: 'Добавьте второй формат в Токены → Page, чтобы сравнить рядом.',
  compareFormatsEmptyTitle: 'Пока нечего сравнивать',
  saveCheckpoint: 'Сохранить чекпоинт',
  restoreCheckpoint: 'Восстановить',
  createDraftFromSandbox: 'Черновик из песочницы',
  exitSandbox: 'Выйти из песочницы',
  discardLocalChanges: 'Сбросить локальные изменения',
  keyboardShortcuts: 'Горячие клавиши',
  editing: 'редактирование',
  pagesCount: 'стр.',
  noPreview: 'Нет превью',
  issuesCount: 'замечаний',
  tools: 'Инструменты',
  toolHintSelect: 'Клик — выбор · протяните пустое место для рамки',
  toolHintHand: 'Тяните, чтобы панорамировать',
  toolHintFrame: 'Протяните на холсте, чтобы нарисовать фрейм',
  toolHintText: 'Протяните на холсте, чтобы нарисовать текст',
  toolHintRect: 'Протяните на холсте, чтобы нарисовать прямоугольник',
  toolHintEllipse: 'Протяните на холсте, чтобы нарисовать эллипс',
  toolHintLine: 'Протяните на холсте, чтобы нарисовать линию',
  toolHintImage: 'Протяните на холсте, чтобы разместить изображение',
  alignSelection: 'Выровнять выделение',
  alignLeft: 'По левому краю',
  alignCenter: 'По центру',
  alignRight: 'По правому краю',
  alignTop: 'По верху',
  alignMiddle: 'По середине',
  alignBottom: 'По низу',
  distributeHorizontal: 'Распределить по горизонтали',
  distributeVertical: 'Распределить по вертикали',
  selectionActions: 'Действия с выделением',
  duplicate: 'Дублировать',
  group: 'Сгруппировать',
  ungroup: 'Разгруппировать',
  lock: 'Заблокировать',
  unlock: 'Разблокировать',
  bringForward: 'На передний план',
  sendBackward: 'На задний план',
  deleteLayerTitle: 'Удалить слой?',
  deleteLayerDescription: 'Этот слой и его дочерние элементы будут удалены.',
};

export const MESSAGES = { en, ru } as const;
export type MessageKey = keyof typeof en;

export function translate(locale: ConstructorLocale, key: MessageKey): string {
  return MESSAGES[locale][key] ?? MESSAGES.en[key];
}

export const KIND_LABEL_KEY: Record<string, MessageKey> = {
  frame: 'kindFrame',
  stack: 'kindStack',
  repeater: 'kindRepeater',
  text: 'kindText',
  image: 'kindImage',
  grid: 'kindGrid',
  divider: 'kindDivider',
  spacer: 'kindSpacer',
  shape: 'kindShape',
  group: 'kindGroup',
  slot: 'kindSlot',
  overlay: 'kindOverlay',
};

export const KIND_HINT_KEY: Record<string, MessageKey> = {
  frame: 'hintFrame',
  stack: 'hintStack',
  repeater: 'hintRepeater',
  text: 'hintText',
  image: 'hintImage',
  grid: 'hintGrid',
  divider: 'hintDivider',
  spacer: 'hintSpacer',
  shape: 'hintShape',
  group: 'hintGroup',
  slot: 'hintSlot',
  overlay: 'hintOverlay',
};

export const PAGE_GROUP_KEY: Record<string, MessageKey> = {
  cover: 'cover',
  toc: 'contents',
  divider: 'sectionDivider',
  category: 'categoryPages',
  spotlight: 'spotlight',
  filler: 'filler',
  info: 'infoPage',
  backCover: 'backCover',
};
